// ─── Ticket Types ────────────────────────────────────────────────────────────

export type TicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_on_client'
  | 'resolved'
  | 'closed';

export type TicketPriority = 'urgent' | 'high' | 'normal' | 'low';

export type TicketCategory =
  | 'bug'
  | 'feature_request'
  | 'general'
  | 'billing'
  | 'technical';

export type TicketActivityType =
  | 'created'
  | 'status_change'
  | 'assigned'
  | 'unassigned'
  | 'replied'
  | 'internal_note'
  | 'priority_change'
  | 'resolved'
  | 'closed'
  | 'reopened';

export interface TicketAttachment {
  name: string;
  url: string;
  size: number;
  type: string;
}

export interface TicketUser {
  id: string;
  name: string;
  email: string;
  avatar?: string | null;
}

export interface Ticket {
  id: string;
  workspace_id: string;
  ticket_number: number;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  created_by: string;
  assigned_to?: string | null;
  client_id?: string | null;
  attachments: TicketAttachment[];
  tags: string[];
  satisfaction_rating?: number | null;
  satisfaction_comment?: string | null;
  first_responded_at?: string | null;
  resolved_at?: string | null;
  closed_at?: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  creator?: TicketUser;
  assignee?: TicketUser;
  client?: TicketUser;
  // Computed
  reply_count?: number;
}

export interface TicketReply {
  id: string;
  ticket_id: string;
  user_id: string;
  content: string;
  is_internal: boolean;
  attachments: TicketAttachment[];
  created_at: string;
  updated_at: string;
  // Joined
  user?: TicketUser;
}

export interface TicketActivity {
  id: string;
  ticket_id: string;
  user_id: string;
  type: TicketActivityType;
  content: string;
  metadata: Record<string, any>;
  created_at: string;
  // Joined
  user?: TicketUser;
}

export interface CannedResponse {
  id: string;
  workspace_id: string;
  title: string;
  content: string;
  category?: string;
  created_by: string;
  created_at: string;
}

export interface TicketStats {
  total: number;
  open: number;
  in_progress: number;
  waiting_on_client: number;
  resolved: number;
  closed: number;
  urgent: number;
  unassigned: number;
  avg_first_response_hours: number | null;
}

// ─── Config ──────────────────────────────────────────────────────────────────

export const TICKET_STATUS_CONFIG: Record<
  TicketStatus,
  { label: string; color: string; dotColor: string }
> = {
  open: {
    label: 'Open',
    color: 'bg-blue-500/10 text-blue-500',
    dotColor: 'bg-blue-500',
  },
  in_progress: {
    label: 'In Progress',
    color: 'bg-workspace-accent/10 text-workspace-accent',
    dotColor: 'bg-workspace-accent',
  },
  waiting_on_client: {
    label: 'Waiting on Client',
    color: 'bg-amber-500/10 text-amber-500',
    dotColor: 'bg-amber-500',
  },
  resolved: {
    label: 'Resolved',
    color: 'bg-emerald-500/10 text-emerald-500',
    dotColor: 'bg-emerald-500',
  },
  closed: {
    label: 'Closed',
    color: 'bg-muted text-muted-foreground',
    dotColor: 'bg-muted-foreground',
  },
};

export const TICKET_PRIORITY_CONFIG: Record<
  TicketPriority,
  { label: string; color: string; dotColor: string }
> = {
  urgent: {
    label: 'Urgent',
    color: 'bg-red-500/10 text-red-500',
    dotColor: 'bg-red-500',
  },
  high: {
    label: 'High',
    color: 'bg-orange-500/10 text-orange-500',
    dotColor: 'bg-orange-500',
  },
  normal: {
    label: 'Normal',
    color: 'bg-blue-500/10 text-blue-500',
    dotColor: 'bg-blue-500',
  },
  low: {
    label: 'Low',
    color: 'bg-muted text-muted-foreground',
    dotColor: 'bg-muted-foreground',
  },
};

export const TICKET_CATEGORY_CONFIG: Record<
  TicketCategory,
  { label: string }
> = {
  bug: { label: 'Bug' },
  feature_request: { label: 'Feature Request' },
  general: { label: 'General' },
  billing: { label: 'Billing' },
  technical: { label: 'Technical Support' },
};
